import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

// Emails granted admin role on first signup/login.
const ADMIN_EMAILS = new Set([
  'firangiz.aslanova@livingchess.net',
  'all_admins@livingchess.net',
]);
const isAdminEmail = (email) => ADMIN_EMAILS.has((email || '').toLowerCase());

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAdminStatus = async (user) => {
    if (!user) {
      console.log('❌ No user, setting isAdmin to false');
      setIsAdmin(false);
      return false;
    }

    try {
      console.log('🔍 Checking admin status for user:', user.email);
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const adminStatus = userData.role === 'admin';
        console.log('✅ User document found:', userData);
        console.log('👑 Admin status:', adminStatus);
        setIsAdmin(adminStatus);
        return adminStatus;
      } else {
        console.log('❌ User document does not exist in Firestore');
        setIsAdmin(false);
        return false;
      }
    } catch (err) {
      console.error('❌ Error checking admin status:', err);
      setIsAdmin(false);
      return false;
    }
  };

  const signup = async (email, password, displayName) => {
    try {
      setError(null);
      console.log('📝 Signing up user:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update profile with display name
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }

      // Determine role
      const role = isAdminEmail(email) ? 'admin' : 'user';
      console.log('🎭 Assigning role:', role);

      // Create user document in Firestore
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userData = {
        email: userCredential.user.email,
        displayName: displayName || '',
        role: role,
        createdAt: new Date().toISOString()
      };

      console.log('💾 Creating Firestore document:', userData);
      await setDoc(userDocRef, userData);
      console.log('✅ Firestore document created successfully');

      // Check admin status
      await checkAdminStatus(userCredential.user);

      return userCredential;
    } catch (err) {
      console.error('❌ Signup error:', err);
      setError(err.message);
      throw err;
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      console.log('🔐 Logging in user:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Check if user document exists, if not create it
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        const role = isAdminEmail(email) ? 'admin' : 'user';
        console.log('📝 User document not found, creating with role:', role);
        await setDoc(userDocRef, {
          email: userCredential.user.email,
          displayName: userCredential.user.displayName || '',
          role: role,
          createdAt: new Date().toISOString()
        });
        console.log('✅ User document created');
      } else if (isAdminEmail(email) && userDoc.data().role !== 'admin') {
        // Auto-promote whitelisted admin emails if they exist but lost the role.
        console.log('🔧 Promoting whitelisted admin email to admin role');
        await setDoc(userDocRef, { ...userDoc.data(), role: 'admin' });
      } else {
        console.log('✅ User document already exists');
      }

      await checkAdminStatus(userCredential.user);
      return userCredential;
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(err.message);
      throw err;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error('❌ Password reset error:', err);
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      setIsAdmin(false);
      await signOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Permanently delete the signed-in user's account: the Firestore profile
  // document (users/{uid}) and the Firebase Authentication account. Firebase
  // requires a recent login to delete an account, so the caller must supply the
  // current password to reauthenticate first.
  const deleteAccount = async (password) => {
    setError(null);
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in.');
    }

    // Reauthenticate to satisfy Firebase's recent-login requirement.
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);

    // Remove the Firestore profile while still authenticated (security rules
    // typically require the owner to be signed in), then delete the auth account.
    await deleteDoc(doc(db, 'users', user.uid));
    await deleteUser(user);

    // onAuthStateChanged will fire with null and clear currentUser/isAdmin.
    setIsAdmin(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await checkAdminStatus(user);
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isAdmin,
    signup,
    login,
    logout,
    resetPassword,
    deleteAccount,
    error,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
